import * as chameleon from '@getgo/chameleon-web';
import { WebChatAvatarType, WithThemeProvider } from '@ucc/cc-common-ui';
import { environment, Environments, getEnvString } from 'environments';
import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { selectAgents } from 'state/Agents/selectors';
import { processMessageEvents } from 'state/Events/actions';
import { store } from 'state/store';
import { useAppDispatch, useAppSelector, useMessageListener } from 'utils/hooks';

import i18nInit, { i18nInstance } from '../i18n';
import { EventsType } from '../state/Events/types';
import Layout from './Layout';

chameleon;

i18nInit('en-US');

const App: FunctionComponent = () => {
  const webChatConfiguration = useAppSelector(({ webChatConfiguration }) => webChatConfiguration);
  const widgetConfiguration = useAppSelector(({ widgetConfiguration }) => widgetConfiguration);
  const events = useAppSelector(({ conversationEvents }) => conversationEvents);
  const agents = useAppSelector(selectAgents);
  const notifications = useAppSelector(({ notifications }) => notifications);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (webChatConfiguration?.language) {
      i18nInstance.changeLanguage(webChatConfiguration?.language ?? 'en-US');
    }
  }, [webChatConfiguration]);

  useMessageListener(
    useCallback(
      (e: MessageEvent) => {
        processMessageEvents(e, dispatch);
      },
      [dispatch]
    )
  );

  useEffect(() => {
    if (getEnvString() === Environments.LOCAL) {
      window.postMessage({
        type: EventsType.CC_WEBCHAT_BOOT,
        payload: environment.appKey,
        origin: 'http://localhost:5000',
      });
    }
  }, [dispatch]);

  const preFetchCustomIconUrl = () => {
    const avatar = webChatConfiguration?.theme?.avatar;
    if (!avatar || avatar.type !== WebChatAvatarType.CUSTOM) return null;

    // just prefetch image
    new Image().src = avatar.customIconCBoxUrl;
    return avatar.customIconCBoxUrl;
  };

  return (
    <Provider store={store}>
      <I18nextProvider i18n={i18nInstance}>
        <WithThemeProvider prefix="webchat">
          {webChatConfiguration && (
            <Layout
              theme={webChatConfiguration?.theme}
              notifications={notifications}
              agents={agents}
              customAvatarIconUrl={preFetchCustomIconUrl()}
              widgetConfiguration={widgetConfiguration}
              events={events}
            />
          )}
        </WithThemeProvider>
      </I18nextProvider>
    </Provider>
  );
};

export default App;
